import { useEffect, useState } from "react"
import { router } from "@inertiajs/react"

import type { KenjossetCart } from "@/components/kenjosset/types"

interface CartProps {
  cart: KenjossetCart
  basketSrc: string
}

// Faithful React port of shared/_cart (generic branch, kenjosset has no
// per-store override). The CSS-only `peer-checked` drawer becomes React state;
// removal goes through the Inertia router (DELETE), which supplies the CSRF
// token automatically.
export default function Cart({ cart, basketSrc }: CartProps) {
  const [open, setOpen] = useState(false)

  const removeItem = (removeUrl: string) =>
    router.delete(removeUrl, { preserveScroll: true, preserveState: true })

  // Auto-open once when the server signals the first item was just added.
  useEffect(() => {
    if (cart.auto_open) setOpen(true)
  }, [cart.auto_open])

  return (
    <div className="ml-6">
      <button
        type="button"
        className="relative cursor-pointer"
        aria-label={cart.title}
        onClick={() => setOpen(true)}
      >
        <img src={basketSrc} alt="" className="h-8 w-8" />
        <span
          id="cart-counter"
          className="bg-primary rounded-full h-6 w-6 absolute text-form-input text-sm -right-2 -bottom-3 text-center align-middle leading-[1.6rem]"
        >
          {cart.count}
        </span>
      </button>

      {open && (
        <div
          className="relative z-10"
          aria-labelledby="slide-over-title"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="fixed inset-0 bg-midgray bg-opacity-75 transition-opacity"
            onClick={() => setOpen(false)}
          />
          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                <div className="pointer-events-auto w-screen max-w-md">
                  <div className="flex h-full flex-col overflow-y-scroll bg-contrast shadow-xl">
                    <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 bg-light">
                      <div className="flex items-start justify-between">
                        <h2 id="slide-over-title" className="text-lg font-medium text-contrast">
                          {cart.title}
                        </h2>
                        <div className="ml-3 flex h-7 items-center">
                          <button
                            type="button"
                            className="w-16 h-16 flex items-center justify-center rounded-full p-3"
                            aria-label="Fermer"
                            onClick={() => setOpen(false)}
                          >
                            <i className="fa-solid fa-xmark ml-6 text-2xl text-contrast cursor-pointer" />
                          </button>
                        </div>
                      </div>

                      <div className="mt-8">
                        <div className="flow-root">
                          <ul role="list" className="-my-6 divide-y divide-content">
                            {cart.items.map((item) => (
                              <li key={item.id} className="flex py-6">
                                <div className="h-24 w-24 flex-shrink-0 overflow-hidden rounded-md border border-content">
                                  {item.image_url ? (
                                    <img
                                      src={item.image_url}
                                      alt={item.name}
                                      className="h-full w-full object-cover object-center"
                                    />
                                  ) : (
                                    <div className="h-full w-full bg-midgray flex items-center justify-center">
                                      <i className="fa-solid fa-image text-content text-2xl" />
                                    </div>
                                  )}
                                </div>
                                <div className="ml-4 flex flex-1 flex-col">
                                  <div>
                                    <div className="flex justify-between text-base font-medium text-contrast">
                                      <h3>{item.name}</h3>
                                      <p className="ml-3">{item.price}</p>
                                    </div>
                                  </div>
                                  <div className="flex flex-1 items-end justify-between text-sm">
                                    <p className="text-midgray">
                                      {cart.quantity_label_template.replace(
                                        "{count}",
                                        String(item.quantity)
                                      )}
                                    </p>
                                    <div className="flex">
                                      <button
                                        type="button"
                                        onClick={() => removeItem(item.remove_url)}
                                        className="font-medium text-primary hover:text-primary/90 cursor-pointer"
                                      >
                                        {cart.remove_label}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-content px-4 py-6 sm:px-6 bg-light">
                      <div className="flex justify-between text-base font-medium text-contrast">
                        <p>{cart.total_label}</p>
                        <p>{cart.total}</p>
                      </div>
                      <p className="mt-0.5 text-sm text-midgray">{cart.shipping_note}</p>
                      <div className="mt-6">
                        <a
                          href={cart.checkout_url}
                          className="flex items-center justify-center rounded-md border border-transparent bg-primary/90 px-6 py-3 text-base font-medium text-light shadow-sm hover:bg-primary"
                        >
                          {cart.payment_button_label}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
